import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase-config";
import { useAuth } from "../contexts/AuthContext";
import type { AgentActionDocument, AgentTaskDocument } from "../types/agentActions";
import { logFirestoreListenerError } from "../utils/firestoreListenerDiagnostics";

interface AuthShape {
  currentUser?: { uid?: string } | null;
}

type AgentActivityCollectionName = "agent_tasks" | "agent_actions";
type AgentActivityOwnershipField = "ownerId" | "userId";
type AgentActivityListenerKey = `${AgentActivityCollectionName}:${AgentActivityOwnershipField}`;
type AgentActivityListenerErrorMap = Partial<Record<AgentActivityListenerKey, Error | null>>;

export interface AgentActivityQuerySpec {
  collectionName: AgentActivityCollectionName;
  ownershipField: AgentActivityOwnershipField;
  projectId: string;
  uid: string;
}

export interface UseAgentTasksReturn {
  tasks: AgentTaskDocument[];
  actionsByTaskId: Record<string, AgentActionDocument[]>;
  loading: boolean;
  error: Error | null;
}

function toMillis(value: unknown): number {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value === "object" && "toMillis" in value && typeof value.toMillis === "function") {
    return value.toMillis();
  }
  if (typeof value === "object" && "toDate" in value && typeof value.toDate === "function") {
    return value.toDate().getTime();
  }
  return 0;
}

function sortByUpdatedAtDesc<T extends { updatedAt?: unknown; createdAt?: unknown }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const bTime = toMillis(b.updatedAt) || toMillis(b.createdAt);
    const aTime = toMillis(a.updatedAt) || toMillis(a.createdAt);
    return bTime - aTime;
  });
}

export function buildAgentActivityQuerySpecs(projectId: string, uid: string): AgentActivityQuerySpec[] {
  if (!projectId || !uid) return [];
  return [
    { collectionName: "agent_tasks", ownershipField: "ownerId", projectId, uid },
    { collectionName: "agent_tasks", ownershipField: "userId", projectId, uid },
    { collectionName: "agent_actions", ownershipField: "ownerId", projectId, uid },
    { collectionName: "agent_actions", ownershipField: "userId", projectId, uid },
  ];
}

export function mergeAgentActivityDocuments<T extends { id: string; updatedAt?: unknown; createdAt?: unknown }>(
  ...groups: T[][]
): T[] {
  const byId = new Map<string, T>();
  for (const group of groups) {
    for (const item of group) {
      byId.set(item.id, item);
    }
  }
  return sortByUpdatedAtDesc(Array.from(byId.values()));
}

function agentActivityListenerKey(spec: Pick<AgentActivityQuerySpec, "collectionName" | "ownershipField">): AgentActivityListenerKey {
  return `${spec.collectionName}:${spec.ownershipField}`;
}

export function firstAgentActivityListenerError(errors: AgentActivityListenerErrorMap): Error | null {
  return Object.values(errors).find((error): error is Error => error instanceof Error) ?? null;
}

function agentActivityCollectionRef(projectId: string, collectionName: AgentActivityCollectionName) {
  if (collectionName === "agent_tasks") {
    return collection(db, "projects", projectId, "agent_tasks");
  }
  return collection(db, "projects", projectId, "agent_actions");
}

export function useAgentTasks(projectId?: string): UseAgentTasksReturn {
  const { currentUser } = useAuth() as AuthShape;
  const uid = currentUser?.uid || "";
  const [tasks, setTasks] = useState<AgentTaskDocument[]>([]);
  const [actions, setActions] = useState<AgentActionDocument[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [actionsLoading, setActionsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !uid) {
      setTasks([]);
      setActions([]);
      setTasksLoading(false);
      setActionsLoading(false);
      setError(null);
      return;
    }

    setTasksLoading(true);
    setActionsLoading(true);
    const taskBuckets: Record<AgentActivityOwnershipField, AgentTaskDocument[]> = { ownerId: [], userId: [] };
    const actionBuckets: Record<AgentActivityOwnershipField, AgentActionDocument[]> = { ownerId: [], userId: [] };
    const pendingTaskFields = new Set<AgentActivityOwnershipField>(["ownerId", "userId"]);
    const pendingActionFields = new Set<AgentActivityOwnershipField>(["ownerId", "userId"]);
    const listenerErrors: AgentActivityListenerErrorMap = {};

    const unsubscribes = buildAgentActivityQuerySpecs(projectId, uid).map((spec) => {
      const listenerKey = agentActivityListenerKey(spec);
      const activityQuery = query(
        agentActivityCollectionRef(spec.projectId, spec.collectionName),
        where(spec.ownershipField, "==", spec.uid),
      );

      return onSnapshot(
        activityQuery,
        (snapshot) => {
          if (spec.collectionName === "agent_tasks") {
            taskBuckets[spec.ownershipField] = snapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            })) as AgentTaskDocument[];
            pendingTaskFields.delete(spec.ownershipField);
            setTasks(mergeAgentActivityDocuments(taskBuckets.ownerId, taskBuckets.userId));
            setTasksLoading(pendingTaskFields.size > 0);
          } else {
            actionBuckets[spec.ownershipField] = snapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            })) as AgentActionDocument[];
            pendingActionFields.delete(spec.ownershipField);
            setActions(mergeAgentActivityDocuments(actionBuckets.ownerId, actionBuckets.userId));
            setActionsLoading(pendingActionFields.size > 0);
          }
          listenerErrors[listenerKey] = null;
          setError(firstAgentActivityListenerError(listenerErrors));
        },
        (err) => {
          logFirestoreListenerError({
            listener: `useAgentTasks.${spec.collectionName}.${spec.ownershipField}`,
            path: `projects/${projectId}/${spec.collectionName}`,
            error: err,
            context: { projectId, uid, ownershipField: spec.ownershipField },
          });
          listenerErrors[listenerKey] = err instanceof Error ? err : new Error("Agent Activity listener failed");
          setError(firstAgentActivityListenerError(listenerErrors));
          if (spec.collectionName === "agent_tasks") {
            pendingTaskFields.delete(spec.ownershipField);
            setTasksLoading(pendingTaskFields.size > 0);
          } else {
            pendingActionFields.delete(spec.ownershipField);
            setActionsLoading(pendingActionFields.size > 0);
          }
        },
      );
    });

    return () => {
      for (const unsubscribe of unsubscribes) unsubscribe();
    };
  }, [projectId, uid]);

  const actionsByTaskId = useMemo(() => {
    const grouped: Record<string, AgentActionDocument[]> = {};
    for (const action of actions) {
      if (!grouped[action.taskId]) grouped[action.taskId] = [];
      grouped[action.taskId].push(action);
    }
    return grouped;
  }, [actions]);

  return {
    tasks,
    actionsByTaskId,
    loading: tasksLoading || actionsLoading,
    error,
  };
}
